"use client";

import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import {
  ChevronRightIcon,
  JetsDeliveryIcon,
  JetsPickupIcon,
  JetsServiceIcon,
  LoaderIcon,
} from "~/icons";
import type { RichJetsLocation } from "~/types";
import { displayPhone } from "~/utils/phone";
import { Box } from "../Box/Box";
import { Text } from "../Text/Text";
import styles from "./LocationCard.module.scss";

function displayAddress(
  address:
    | {
        locality: string | null;
        region: string | null;
        postalCode: string | null;
      }
    | null
    | undefined,
  jetsName: string | undefined,
): string {
  if (!address) return "";
  const cityState = [address.locality, address.region]
    .filter(Boolean)
    .join(", ");

  const addressLine = [cityState, address.postalCode].filter(Boolean).join(" ");
  return jetsName ? `${addressLine} (${jetsName})` : addressLine;
}

export function LocationCard({
  loc,
  skeleton,
  distance,
}: {
  loc?: RichJetsLocation;
  skeleton?: boolean;
  distance?: number | null;
}) {
  const [pressed, setPressed] = useState(false);

  return (
    <Box
      background="white"
      border="standard"
      borderColor={pressed ? "accent" : "neutral-300"}
      borderRadius="rounded"
      onClick={() => setPressed(true)}
      paddingX="4"
      paddingY="2"
      marginX="2"
      marginY="1"
      display="flex"
      alignItems="center"
    >
      <Box flexGrow={1}>
        {(skeleton || loc?.address?.street) && (
          <Text
            color="neutral-900"
            font="special"
            weight="semi-strong"
            size="xlarge"
          >
            {skeleton ? <Skeleton width={160} /> : loc?.address?.street}
          </Text>
        )}

        {(skeleton || loc?.address) && (
          <Text
            color="neutral-900"
            font="special"
            weight="regular"
            size="standard"
          >
            {skeleton ? (
              <Skeleton width={160} />
            ) : (
              displayAddress(loc?.address, loc?.jetsName)
            )}
          </Text>
        )}

        <Box paddingY="1">
          {(skeleton || loc?.storeLocalReference) && (
            <Text
              color="neutral-500"
              font="special"
              weight="semi-strong"
              size="standard"
            >
              {skeleton ? <Skeleton width={160} /> : loc?.storeLocalReference}
            </Text>
          )}

          {(skeleton || loc?.phoneNumber) && (
            <Text
              color="neutral-500"
              font="special"
              weight="regular"
              size="standard"
            >
              {skeleton ? (
                <Skeleton width={160} />
              ) : (
                displayPhone(loc?.phoneNumber)
              )}
            </Text>
          )}
        </Box>
        {!skeleton &&
          (loc?.orderAheadEnabled ||
            loc?.orderDeliveryEnabled ||
            loc?.orderAtTableEnabled) && (
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="spaceBetween"
              alignItems="flexEnd"
            >
              <Box display="flex">
                {loc?.orderAheadEnabled && (
                  <IconContainer>
                    <JetsPickupIcon />
                  </IconContainer>
                )}
                {loc?.orderDeliveryEnabled && (
                  <IconContainer>
                    <JetsDeliveryIcon />
                  </IconContainer>
                )}
                {loc?.orderAtTableEnabled && (
                  <IconContainer>
                    <JetsServiceIcon />
                  </IconContainer>
                )}
              </Box>
            </Box>
          )}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flexEnd"
        style={{ alignSelf: "stretch" }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexGrow={1}
        >
          {skeleton ? (
            <Skeleton width={24} height={24} />
          ) : pressed ? (
            <LoaderIcon />
          ) : (
            <ChevronRightIcon fill="var(--color-primary)" />
          )}
        </Box>
        {/* Placeholder text prevents layout shift with Icon */}
        <Text
          color="accent"
          font="special"
          weight="semi-strong"
          size="standard"
          whiteSpace="nowrap"
          visibility={distance != null ? "visible" : "hidden"}
        >
          {distance != null ? `${distance.toFixed(1)} mi` : "0.0 mi"}
        </Text>
      </Box>
    </Box>
  );
}

function IconContainer({ children }: { children: React.ReactNode }) {
  return <div className={styles.iconContainer}>{children}</div>;
}
